import { useMemo } from 'react';
import { EPaletteModes } from '@/types/theme-packs.types';
import isColorLightOrDark from '@/utils/colors/is-color-light-or-dark';
import Container from '@ui/container/container';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';

const listMemberName = (firstName?: string, lastName?: string) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else {
    return firstName || lastName || 'Name not set';
  }
};

const getInitials = (firstName?: string, lastName?: string) => {
  const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
  return firstInitial + lastInitial || '?';
};

type ProfileTableCell = {
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
};

const ProfileTableCell = ({ firstName, lastName, email, avatar }: ProfileTableCell) => {
  const getDeterministicColor = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 60%)`;
  };

  const { backgroundColor, textColor } = useMemo(() => {
    const colorString = `${firstName}${lastName}${email}`;
    const bg = getDeterministicColor(colorString);
    let txt = 'var(--gray-100)';
    if (bg) {
      if (isColorLightOrDark(bg) === EPaletteModes.Light) {
        txt = 'var(--gray-1200)';
      }
    }
    return { backgroundColor: bg, textColor: txt };
  }, [firstName, lastName, email]);

  return (
    <>
      <Container className="flex jc-flex-start ai-center gap-8" maxWidth="170px">
        <OptionalComponent display={!!avatar}>
          <Container
            className="L1 square-32 radius-full overflow-hidden"
            backgroundImage={avatar || undefined}
          ></Container>
        </OptionalComponent>
        <OptionalComponent display={!avatar}>
          <Container className="flex jc-center ai-center square-32 radius-full" background={backgroundColor}>
            <Text className="L2" color={textColor}>
              {getInitials(firstName, lastName)}
            </Text>
          </Container>
        </OptionalComponent>

        <Container className="flex-column jc-flex-start flex-1" width="100%">
          <Text clamp={1}>{listMemberName(firstName, lastName)}</Text>
          <Text clamp={1}>{email || 'Email not set'}</Text>
        </Container>
      </Container>
    </>
  );
};

export default ProfileTableCell;
